import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Download,
  Route,
} from 'lucide-react';
import { toast } from 'sonner';
import { departmentLabel, type Attachment, type Department } from '@refdash/api-client';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CountdownBadge } from '@/components/countdown-badge';
import { CaseTimeline } from '@/components/case-timeline';
import { DOC_TYPE_LABELS } from '@/lib/case-labels';
import { PatientInfoCard } from '@/components/patient-info-card';
import { ChatDecision, scopeToAuthority } from '@/components/chat-decision';
import { cn } from '@/lib/utils';

function getFileIcon(mime: string) {
  if (mime.startsWith('image/')) return Image;
  if (mime.includes('pdf')) return FileText;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return FileSpreadsheet;
  return File;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Hand a mis-routed case to the right department before anyone decides it.
 *
 * Routing is resolved once, when the case is filed, from the specialty string
 * GlobeMed sends. When no rule matches — or the wrong one does — this is the
 * only way to move the case without deciding it. Hospital-wide roles only:
 * the same authority that can already route a case by accepting it. */
function MoveDepartment({
  approvalId,
  currentDepartmentId,
  departments,
  onMoved,
}: {
  approvalId: number;
  currentDepartmentId: number | null;
  departments: Department[];
  onMoved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string>(
    currentDepartmentId != null ? String(currentDepartmentId) : '',
  );

  const move = useMutation({
    mutationFn: () =>
      api.approvals.moveDepartment(approvalId, target === '' ? null : Number(target)),
    onSuccess: () => {
      const name = departments.find((d) => String(d.id) === target)?.name;
      toast.success(name ? `Moved to ${name}` : 'Sent back to the coordinator queue');
      setOpen(false);
      onMoved();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const current = departments.find((d) => d.id === currentDepartmentId);

  return (
    <div className="mb-4 rounded-2xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Route className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Department
            </p>
            <p className="truncate text-sm font-semibold text-foreground">
              {current ? departmentLabel(current) : 'Unassigned — coordinator queue'}
            </p>
          </div>
        </div>
        {!open && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            Move
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="h-10 min-w-52 flex-1 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary"
          >
            <option value="">Unassigned — coordinator queue</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{departmentLabel(d)}</option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={
              move.isPending
              || (target === '' ? currentDepartmentId == null : Number(target) === currentDepartmentId)
            }
            onClick={() => move.mutate()}
          >
            {move.isPending ? 'Moving…' : 'Confirm'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

export function ApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const hospitalId = user?.hospital_id ?? '';

  const approvalId = Number(id);

  const { data: approvals, isLoading: loadingApprovals } = useQuery({
    queryKey: ['approvals', 'pending', hospitalId],
    queryFn: () => api.approvals.listPending(hospitalId || undefined),
  });

  const approval = approvals?.find((a) => a.id === approvalId);

  const { data: doctors = [], isLoading: loadingDoctors } = useQuery({
    queryKey: ['doctors', hospitalId],
    queryFn: () => api.hospitals.listDoctors(hospitalId),
    enabled: !!hospitalId,
  });

  const { data: departments = [], isLoading: loadingDepts } = useQuery({
    queryKey: ['departments', hospitalId],
    queryFn: () => api.hospitals.listDepartments(hospitalId),
    enabled: !!hospitalId,
  });

  // Offer only what this user's role may submit — a doctor sees themselves,
  // a department head sees their department. The server still enforces it.
  const scoped = scopeToAuthority(
    {
      role: user?.role ?? '',
      departmentId: user?.department_id ?? null,
      doctorId: user?.doctor_id ?? null,
    },
    doctors,
    departments,
  );

  // Moving a case is routing, not deciding — so it belongs to the roles with
  // hospital-wide sight. A head or doctor only ever sees their own department,
  // and handing the case away would put it somewhere they can't follow it.
  const canMove = ['hospital_rep', 'coordinator', 'operator', 'admin']
    .includes(user?.role ?? '');

  const isLoading = loadingApprovals || loadingDoctors || loadingDepts;

  if (isLoading) {
    return (
      <div className="px-5 py-5 lg:px-8 lg:py-8 max-w-lg lg:max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-12 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="px-5 py-20 text-center max-w-lg lg:max-w-3xl mx-auto">
        <p className="text-muted-foreground">Approval not found or already decided.</p>
        <Button
          variant="ghost"
          className="mt-4 gap-2"
          onClick={() => navigate('/approvals')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Approvals
        </Button>
      </div>
    );
  }

  const attachments = approval.attachments ?? [];

  return (
    <div className="px-5 pt-5 pb-24 lg:px-8 lg:pt-8 max-w-lg lg:max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-5"
      >
        <button
          onClick={() => navigate('/approvals')}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <CountdownBadge expiresAt={approval.timer_expires_at} size="md" />
      </motion.div>

      {/* Reference badge */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-4"
      >
        <span className="font-mono text-xs font-semibold text-muted-foreground tracking-wider uppercase bg-secondary px-2.5 py-1 rounded-md">
          {approval.ihalaty_reference}
        </span>
      </motion.div>

      {/* Patient info — always fully visible */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-4"
      >
        <PatientInfoCard
          info={approval.patient_info}
          reference={approval.ihalaty_reference}
        />
      </motion.div>

      {/* Case timeline — how old the case is and how long it has waited */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18 }}
        className="mb-4"
      >
        <CaseTimeline
          createdAt={approval.created_at}
          timerExpiresAt={approval.timer_expires_at}
          status={approval.status}
        />
      </motion.div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
            Attachments ({attachments.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((att: Attachment) => {
              const Icon = getFileIcon(att.mime_type);
              const url = api.approvals.getAttachmentUrl(
                att.id,
                att.filename,
              );
              return (
                <div
                  key={att.id}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 rounded-xl',
                    'border border-border bg-card',
                  )}
                >
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 flex-1 min-w-0 hover:text-primary transition-colors"
                  >
                    <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate max-w-[180px]">
                        {att.filename}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {att.attachment_type && DOC_TYPE_LABELS[att.attachment_type]
                          ? `${DOC_TYPE_LABELS[att.attachment_type]} · `
                          : ''}
                        {formatFileSize(att.file_size)}
                      </p>
                    </div>
                  </a>
                  <a
                    href={`${url}?download=1`}
                    className="p-1.5 rounded-lg hover:bg-primary-50 transition-colors shrink-0"
                    title={`Download ${att.filename}`}
                  >
                    <Download className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                  </a>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Re-route — only the hospital-wide roles, and only before a decision */}
      {canMove && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
        >
          <MoveDepartment
            approvalId={approval.id}
            currentDepartmentId={approval.department_id}
            departments={departments}
            onMoved={() => queryClient.invalidateQueries({ queryKey: ['approvals'] })}
          />
        </motion.div>
      )}

      {/* Chat decision flow */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <ChatDecision
          approvalId={approval.id}
          doctors={scoped.doctors}
          departments={scoped.departments}
          onDecided={() => {
            queryClient.invalidateQueries({ queryKey: ['approvals'] });
            navigate('/approvals');
          }}
        />
      </motion.div>
    </div>
  );
}
